import React, { useState } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
  doc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../style/Transaction.css';
import * as XLSX from 'xlsx';


const TotalDeposits = () => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchDepositTransactions = async (from, to) => {
    const result = [];
    let totalAmount = 0;

    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));

      for (const userDoc of usersSnapshot.docs) {
        const userId = userDoc.id;
        const brandName = userDoc.data().brandName || 'Unknown';

        const transactionsRef = collection(
          db,
          'users',
          userId,
          'wallet',
          'walletDetails',
          'transactions'
        );

        const q = query(
          transactionsRef,
          where('type', '==', 'deposit'),
          where('timestamp', '>=', Timestamp.fromDate(from)),
          where('timestamp', '<=', Timestamp.fromDate(to))
        );

        const txSnapshot = await getDocs(q);

        txSnapshot.forEach((txDoc) => {
          const data = txDoc.data();
          result.push({
            id: txDoc.id,
            ...data,
            brandName,
            userId,
          });

          if (data.amount) {
            totalAmount += data.amount;
          }
        });
      }

      setTransactions(result);
      setTotal(totalAmount);
    } catch (error) {
      console.error('Error fetching deposits:', error);
      toast.error('Error fetching deposit transactions');
    } finally {
      setLoading(false);
    }
  };

  // const downloadExcelReport = () => {
  //   if (transactions.length === 0) {
  //     toast.info('No transactions to download');
  //     return;
  //   }
  
  //   const dataForExcel = transactions.map((tx) => ({
  //     'Brand Name': tx.brandName,
  //     'Amount (₹)': tx.amount?.toFixed(2),
  //     'Balance Before': tx.balanceBefore?.toFixed(2),
  //     'Balance After': tx.balanceAfter?.toFixed(2),
  //     'Timestamp': tx.timestamp?.seconds
  //       ? new Date(tx.timestamp.seconds * 1000).toLocaleString()
  //       : 'N/A',
  //   }));
  
  //   const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
  //   const workbook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(workbook, worksheet, 'Deposits Report');
  
  //   const fileName = `Deposits_Report_${fromDate}_to_${toDate}.xlsx`;
  //   XLSX.writeFile(workbook, fileName);
  // };
  

   const downloadExcelReport = () => {
    if (transactions.length === 0) {
      toast.info('No transactions to download');
      return;
    }
  
    // Group transactions by brand and calculate totals
    const brandTotals = {};
    transactions.forEach((tx) => {
      const brand = tx.brandName;
      if (!brandTotals[brand]) {
        brandTotals[brand] = 0;
      }
      brandTotals[brand] += tx.amount || 0;
    });

    // Create summary data with brands and their totals
    const summaryData = Object.entries(brandTotals).map(([brand, total]) => ({
      'Brand Name': brand,
      'Total Amount (₹)': total.toFixed(2),
    }));

    // Add grand total row
    summaryData.push({
      'Brand Name': 'GRAND TOTAL',
      'Total Amount (₹)': total.toFixed(2),
    });

    // Create detailed transactions data
    const detailedData = transactions.map((tx) => ({
      'Brand Name': tx.brandName,
      'Amount (₹)': tx.amount?.toFixed(2),
      'Balance Before': tx.balanceBefore?.toFixed(2),
      'Balance After': tx.balanceAfter?.toFixed(2),
      'Timestamp': tx.timestamp?.seconds
        ? new Date(tx.timestamp.seconds * 1000).toLocaleString()
        : 'N/A',
    }));
  
    // Create workbook with two sheets
    const workbook = XLSX.utils.book_new();
    
    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Brand Summary');
    
    const detailSheet = XLSX.utils.json_to_sheet(detailedData);
    XLSX.utils.book_append_sheet(workbook, detailSheet, 'Detailed Transactions');
  
    const fileName = `Deposits_Report_${fromDate}_to_${toDate}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!fromDate || !toDate) {
      toast.error('Please select both From and To dates');
      return;
    }

    // Create date objects and set time ranges for full day coverage
    const from = new Date(fromDate);
    from.setHours(0, 0, 0, 0); // Start of the day
    
    const to = new Date(toDate);
    to.setHours(23, 59, 59, 999); // End of the day

    if (from > to) {
      toast.error('"From Date" should be earlier than "To Date"');
      return;
    }

    setLoading(true);
    fetchDepositTransactions(from, to);
  };

  return (
    <div className="transactions-page">
      <ToastContainer position="top-center" autoClose={4000} />
      <h2>Total Deposits</h2>

      <button onClick={downloadExcelReport} className="download-btn">
        Download Report
      </button>

      <form onSubmit={handleSubmit} className="date-filter-form">
        <label>
          From:
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            required
          />
        </label>
        <label>
          To:
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            required
          />
        </label>
        <button type="submit">Fetch Deposits</button>
      </form>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <h3>Total Amount: ₹{total.toFixed(2)}</h3>

          <table className="transactions-table">
            <thead>
              <tr>
                <th>Brand Name</th>
                <th>Amount (₹)</th>
                <th>Balance Before</th>
                <th>Balance After</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length > 0 ? (
                transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td>{tx.brandName}</td>
                    <td>{tx.amount?.toFixed(2)}</td>
                    <td>{tx.balanceBefore?.toFixed(2)}</td>
                    <td>{tx.balanceAfter?.toFixed(2)}</td>
                    <td>
                      {tx.timestamp?.seconds
                        ? new Date(tx.timestamp.seconds * 1000).toLocaleString()
                        : 'N/A'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5">No transactions found</td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default TotalDeposits;